import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  ArrowRight,
  FolderKanban,
  ListTodo,
  AlertTriangle,
} from 'lucide-react';
import { useDashboard } from '@/hooks/use-dashboard';
import { TimeBudgetBar } from '@/components/my-day/time-budget-bar';
import { DashboardSkeleton } from '@/components/common/skeletons';
import { OnboardingWizard } from '@/components/onboarding/onboarding-wizard';

export const Route = createFileRoute('/_authenticated/')({
  component: DashboardPage,
});

function DashboardPage() {
  const { data, isLoading } = useDashboard();
  const { setActiveWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();

  const handleWorkspaceClick = (id: string) => {
    setActiveWorkspace(id);
    navigate({ to: '/workspace/$workspaceId/projects', params: { workspaceId: id } });
  };

  const myDay = data?.myDaySummary;
  const workspaces = data?.workspaces ?? [];

  const hasProjects = workspaces.some(
    (ws) => ws.activeProjects > 0 || ws.openTasks > 0
  );
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('triankl-onboarding-dismissed') === 'true'
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Welcome to Triankl OS</h1>
        <p className="text-muted-foreground">Your workspaces and daily overview.</p>
      </div>

      {isLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          {/* My Day Summary Card */}
          {myDay && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Calendar className="h-4 w-4" />
                  My Day
                </CardTitle>
                <Link to="/my-day">
                  <Button variant="ghost" size="sm" className="h-7 text-xs">
                    Open <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-4 text-sm">
                  <span>
                    <span className="font-semibold">{myDay.totalCount}</span> tasks planned
                  </span>
                  <span>
                    <span className="font-semibold">{myDay.completedCount}</span> done
                  </span>
                </div>
                <TimeBudgetBar
                  compact
                  summary={{
                    totalMinutes: myDay.totalMinutes,
                    availableMinutes: myDay.availableMinutes,
                    unestimatedCount: 0,
                    completedCount: myDay.completedCount,
                    totalCount: myDay.totalCount,
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Workspace Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {workspaces.map((ws) => (
              <Card
                key={ws.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => handleWorkspaceClick(ws.id)}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium">{ws.name}</CardTitle>
                  <Badge variant="outline" className="text-[10px]">
                    {ws.type}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <FolderKanban className="h-3.5 w-3.5" />
                      <span>{ws.activeProjects} active</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <ListTodo className="h-3.5 w-3.5" />
                      <span>{ws.openTasks} open</span>
                    </div>
                    {ws.overdueTasks > 0 && (
                      <div className="flex items-center gap-1.5 text-red-500">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span>{ws.overdueTasks} overdue</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {!hasProjects && !dismissed && workspaces.length > 0 && (
        <OnboardingWizard
          workspaceId={workspaces[0].id}
          open={true}
          onComplete={() => setDismissed(true)}
          onDismiss={() => {
            localStorage.setItem('triankl-onboarding-dismissed', 'true');
            setDismissed(true);
          }}
        />
      )}
    </div>
  );
}
